import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { IconRead } from '../../types/api';
import { fetchIcons } from './generationApi';

/**
 * The curated icon list, loaded once per session.
 *
 * Global and unchanging while the app is open, so the fetch is cached in a
 * module-level promise exactly like `useIntents`: every editor that opens is
 * one request between them. A failed load clears the cache, so the next mount
 * really retries.
 */
let iconsPromise: Promise<IconRead[]> | null = null;

function loadIcons(): Promise<IconRead[]> {
  if (iconsPromise === null) {
    iconsPromise = fetchIcons().catch((caught: unknown) => {
      iconsPromise = null;
      throw caught;
    });
  }
  return iconsPromise;
}

export interface IconsResult {
  /** The icons in the server's curated order. Empty while loading or on failure. */
  icons: IconRead[];
  isLoading: boolean;
  error: string | null;
}

/** Stable empty result, so consumers do not see a new array identity per render. */
const NO_ICONS: never[] = [];

/**
 * The icons behind the layer editor's Icon control (IMAGE_VARIETY_PLAN §14 S6).
 *
 * Shape follows {@link useIntents}. A failure is not fatal: the editor still
 * offers "No icon" and still shows a slide's current icon by name.
 */
export function useIcons(): IconsResult {
  const [loaded, setLoaded] = useState<IconRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const icons = await loadIcons();
        if (cancelled) return;
        setLoaded(icons);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        setLoaded(NO_ICONS);
        setError(getErrorMessage(caught, 'Could not load the icon list.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    icons: loaded ?? NO_ICONS,
    isLoading: loaded === null,
    error,
  };
}
